import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, FileText, RotateCcw, ChevronLeft, Home } from "lucide-react";
import Seo from "../../components/Seo";

const MKT_GOLD = "#D97706";
const NAVY = "#0B1E36";
const VIGENCIA = "Julio 2026";

const DOCS = {
  terminos: {
    icon: FileText,
    titulo: "Términos y Condiciones",
    resumen: "Reglas de uso de la plataforma ConstruLink y su Marketplace de recursos digitales.",
    secciones: [
      { h: "1. Aceptación", p: "Al registrarte y utilizar ConstruLink aceptas estos Términos y Condiciones, así como nuestra Política de Privacidad y Política de Reembolsos. Si no estás de acuerdo, no debes usar la plataforma." },
      { h: "2. Descripción del servicio", p: "ConstruLink es una plataforma integral para la industria de la construcción que ofrece red de contactos, software, academia y un Marketplace donde vendedores independientes publican productos digitales y cursos. ConstruLink actúa como intermediario tecnológico y de pagos." },
      { h: "3. Cuentas de usuario", p: "Eres responsable de la veracidad de tus datos y de la seguridad de tus credenciales. Debes ser mayor de edad y tener capacidad legal para contratar. Podemos suspender cuentas que incumplan estos términos o realicen usos fraudulentos." },
      { h: "4. Vendedores independientes", p: "Los productos y cursos del Marketplace son creados y ofrecidos por vendedores independientes, quienes garantizan la titularidad y los derechos del contenido publicado. ConstruLink no es autor de dicho contenido y aplica un proceso de moderación previo a su publicación." },
      { h: "5. Compras y pagos", p: "Los pagos se procesan mediante Mercado Pago. Los precios se muestran en la moneda indicada e incluyen la comisión de la plataforma. Tras la confirmación del pago, el comprador obtiene acceso al recurso desde \u00abMis Compras\u00bb." },
      { h: "6. Comisiones", p: "ConstruLink cobra una comisión por cada venta según el plan del vendedor (Básico, Pro o Elite). El saldo neto queda disponible para retiro según la política de comisiones vigente." },
      { h: "7. Propiedad intelectual", p: "El contenido adquirido es para uso personal según la licencia indicada por el vendedor. Queda prohibida la reventa, redistribución o reproducción no autorizada." },
      { h: "8. Conducta prohibida", p: "No está permitido subir contenido ilegal, malicioso o que infrinja derechos de terceros, ni intentar vulnerar la seguridad de la plataforma." },
      { h: "9. Limitación de responsabilidad", p: "ConstruLink no se responsabiliza por daños derivados del uso del contenido de vendedores independientes, dentro de los límites permitidos por la ley aplicable." },
      { h: "10. Cambios y ley aplicable", p: "Podemos actualizar estos términos notificando a los usuarios. Se rigen por las leyes de la República del Perú." },
    ],
  },
  privacidad: {
    icon: ShieldCheck,
    titulo: "Política de Privacidad",
    resumen: "Cómo recopilamos, usamos y protegemos tus datos personales en ConstruLink.",
    secciones: [
      { h: "1. Datos que recopilamos", p: "Datos de registro (nombre, correo, rol, teléfono, ubicación), datos de tu perfil profesional, contenido que publicas, e información técnica (dispositivo, IP, cookies) necesaria para operar el servicio." },
      { h: "2. Finalidad del tratamiento", p: "Usamos tus datos para prestar el servicio, procesar compras y pagos, personalizar tu experiencia, prevenir fraudes, cumplir obligaciones legales y comunicarte información relevante." },
      { h: "3. Base legal", p: "Tratamos tus datos con base en la ejecución del contrato de servicio, tu consentimiento, nuestro interés legítimo y el cumplimiento de obligaciones legales." },
      { h: "4. Compartición de datos", p: "Compartimos datos con proveedores que nos permiten operar (por ejemplo, Mercado Pago para pagos y proveedores de correo/almacenamiento), bajo acuerdos de confidencialidad. No vendemos tus datos personales." },
      { h: "5. Almacenamiento y seguridad", p: "Aplicamos medidas técnicas y organizativas razonables para proteger tu información. Los archivos se almacenan mediante servicios de almacenamiento seguros con acceso controlado." },
      { h: "6. Tus derechos", p: "Puedes acceder, rectificar, actualizar o solicitar la eliminación de tus datos, así como oponerte a ciertos tratamientos, escribiéndonos a soporte@construlink.pe." },
      { h: "7. Retención", p: "Conservamos tus datos mientras tu cuenta esté activa y durante los plazos legales aplicables. Los registros de auditoría se conservan por periodos limitados." },
      { h: "8. Cookies", p: "Usamos cookies y almacenamiento local para mantener tu sesión y mejorar la experiencia. Puedes gestionarlas desde tu navegador." },
      { h: "9. Contacto", p: "Para cualquier consulta sobre privacidad, escríbenos a soporte@construlink.pe." },
    ],
  },
  reembolsos: {
    icon: RotateCcw,
    titulo: "Política de Reembolsos",
    resumen: "Condiciones para solicitar el reembolso de productos digitales y cursos del Marketplace.",
    secciones: [
      { h: "1. Naturaleza de los productos", p: "El Marketplace ofrece productos digitales y cursos de acceso inmediato. Por su naturaleza descargable, aplican condiciones especiales de reembolso descritas a continuación." },
      { h: "2. Plazo para solicitar", p: "Puedes solicitar un reembolso únicamente dentro de los 3 (tres) días calendario posteriores a la compra." },
      { h: "3. Condición de no descarga", p: "El reembolso procede solo si aún NO has descargado el material del recurso adquirido. Una vez descargado el material, la compra no es reembolsable." },
      { h: "4. Cómo solicitarlo", p: "Ingresa a \u00abMis Compras\u00bb, ubica el recurso y presiona \u00abSolicitar reembolso\u00bb indicando el motivo. Nuestro equipo evaluará la solicitud." },
      { h: "5. Evaluación", p: "ConstruLink revisará que se cumplan las condiciones (plazo y no descarga). Si se aprueba, se revocará tu acceso al recurso y se ajustará el saldo del vendedor." },
      { h: "6. Tiempo de devolución del dinero", p: "La devolución del dinero se realizará en un plazo máximo de 30 (treinta) días, a través del medio de pago utilizado en la compra." },
      { h: "7. Casos no elegibles", p: "No son reembolsables: recursos gratuitos, solicitudes fuera de plazo, y recursos cuyo material ya haya sido descargado." },
      { h: "8. Contacto", p: "Para dudas sobre un reembolso, escríbenos a soporte@construlink.pe." },
    ],
  },
};

const NAV = [
  { key: "terminos", label: "Términos y Condiciones", to: "/legal/terminos" },
  { key: "privacidad", label: "Política de Privacidad", to: "/legal/privacidad" },
  { key: "reembolsos", label: "Política de Reembolsos", to: "/legal/reembolsos" },
];

export default function LegalPage({ tipo }) {
  const doc = DOCS[tipo] || DOCS.terminos;
  const Icon = doc.icon;
  return (
    <div className="min-h-screen bg-slate-50" data-testid={`legal-${tipo}`}>
      <Seo title={doc.titulo} description={doc.resumen} />
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between">
          <Link to="/tienda" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-900" data-testid="legal-volver">
            <ChevronLeft size={16} /> Volver al Marketplace
          </Link>
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-900">
            <Home size={15} /> Inicio
          </Link>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-wrap gap-2 mb-8" data-testid="legal-nav">
          {NAV.map((n) => (
            <Link key={n.key} to={n.to} data-testid={`legal-nav-${n.key}`}
              className="text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors"
              style={n.key === tipo ? { backgroundColor: MKT_GOLD, color: "#fff", borderColor: MKT_GOLD } : { color: "#475569", borderColor: "#E2E8F0" }}>
              {n.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <span className="w-12 h-12 rounded-xl flex items-center justify-center text-white" style={{ backgroundColor: NAVY }}><Icon size={24} /></span>
          <div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900">{doc.titulo}</h1>
            <p className="text-xs text-slate-400">Vigencia: {VIGENCIA}</p>
          </div>
        </div>
        <p className="mt-4 text-slate-600">{doc.resumen}</p>

        <div className="mt-8 space-y-6">
          {doc.secciones.map((s, i) => (
            <section key={i} data-testid={`legal-seccion-${i}`}>
              <h2 className="text-base font-bold text-slate-900">{s.h}</h2>
              <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{s.p}</p>
            </section>
          ))}
        </div>

        <p className="mt-10 text-xs text-slate-400 border-t border-slate-200 pt-6">
          © {new Date().getFullYear()} ConstruLink. Este documento es un texto estándar y puede actualizarse. Para consultas: soporte@construlink.pe
        </p>
      </div>
    </div>
  );
}
